/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import { Grid } from '@/components/Grid';
import styles from './styles.module.scss';
import Image, { pureLoader } from '@/components/NextImage';
import root from 'window-or-global';
import azCommonStyles from '@/theme/globals.module.scss';
import { replaceHistoryStateOptionsOnClick } from '@/features/common/utils/replaceHistoryStateOptionsOnClick';
import { historyStateOptionAttributes } from '@/constants/historyStateOption';
import { useActiveElement } from '@/hooks/useActiveElement';
import { useLabels } from '@/hooks/useLabels';
import { Button } from '@/components/Button';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useAddToCart } from '@/features/orders/api/postAddToCart';
import { useAddToCartWithMiniCart } from '../../hooks/useAddToCartWithMiniCart';
import type {
  AddCertonaItemObjectType,
  AddToCartRequestType,
  LargePartCardProps,
} from '../../interface';
import { SmartLink as Link } from '@/utils/smartLink';
import { Rating } from '@/components/Rating/Rating';
import { LoadingIndicatorDots } from '@/components/LoadingIndicatorDots';
import { usePaymentOptionsMutation } from '@/features/checkout/api/getPaymentOptions';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { routePaths } from '@/constants/routePaths';
import { useDeviceType } from '@/utils/useDeviceType';
import { formatPrice } from '@/utils/validator/formatPrice';
import { type OrderType } from '@/constants/orderTypes';
import type { CustomLinkName } from '@/types/analytics';
import { Text, View, Skeleton } from '@az/starc-ui';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { getPageTypeDisplayName } from '@/utils/analytics/track/trackRecommendationsPageOrZone';
import { useRouter } from 'next/navigation';
import { useFeatureFlag } from '@/features/globalConfig/hooks/useFeatureFlag';

const labelMap = {
  priceUnavailable: 'label_ProductDisplayPage_body_PriceUnavailable',
  lblCarouselImage: 'label_Carousel_Image',
  lblAdd: 'label_MyAccountVehicle_maintenanceInterval_Add',
  lblToCart: 'label_ProductDisplayPage_body_ToCart',
  lblAddToCart: 'label_add_to_cart_certona',
  lblAdded: 'label_added_to_cart_certona',
  lblTotalReviews: 'label_Carousel_TotalReviews',
  lblSeeOptions: 'label_certonaSeeOptions',
};

/**
 * Helper function to generate the product recommendation type string
 * @param pageType - The current page type from utag_data
 * @param recTabNumber - The tab number extracted from container_id (1, 2, 3, or 4)
 * @returns The formatted product recommendation type string
 */
function getProductRecommendationType(
  pageType: string | undefined,
  recTabNumber: number | null | undefined,
): string {
  const pageTypeDisplay = getPageTypeDisplayName(pageType);

  if (recTabNumber) {
    return `${pageTypeDisplay}: NavigatorRex_Tab${recTabNumber}`;
  }

  return 'Complete the Job';
}

export function LargePartCard(props: LargePartCardProps) {
  const {
    part: { properties, label, totalReviews, averageRating, isDeal },
    actionHandler,
    itemId = '',
    pricingInfo,
    dynamicRef,
    onkeydown,
    addToCartAnalyticsandSuccessMessage,
    showCard = true,
    addToCartData,
    setAddedCertonaItemObject,
    showAddToCartCTA,
    shouldShowAddToCart = true,
    isLoadingAddToCart,
    recTabNumber,
    certonaScheme,
    dealsData,
    isDealsLoading,
    visibleDealSkuIds,
  } = props;
  const { priceUnavailable, lblAdd, lblToCart, lblAdded, lblTotalReviews, lblSeeOptions } =
    useLabels(labelMap);
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const isCart = legacyRouter.pathname === '/cart';
  const isVehicleDetails = legacyRouter.asPath.includes(routePaths.vehicleDetails);
  const deviceType = useDeviceType();
  const isBot = deviceType === 'bot';
  const certonaAddToCartEnabled = !isBot && showAddToCartCTA;
  const { mutateAsync: fetchPaymentOptions } = usePaymentOptionsMutation();
  const [showLoadingIndicators, setShowLoadingIndicators] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const addProductToCart = `${lblAdd} ${label} ${lblToCart}`;
  const addedProductToCart = `${lblAdded} ${label} ${lblToCart}`;
  const seeOptionsForProduct = `${lblSeeOptions} ${label}`;
  const { corePrice, retailPrice, totalPrice } = pricingInfo ?? {};
  const focusedElement = useActiveElement();
  const { data: headerData } = useHeaderData();
  const skusPresentInCart = headerData?.analyticsData?.cartProductSkus;
  const catalogVehicleId = headerData?.vehicleMap?.catalogVehicleId ?? '';
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { setPageSpecificData } = usePageSpecificData();
  const isDealsEnabled = useFeatureFlag('IS_DEALS_PRODUCT_RECS_ENABLED') === 'true';
  const skuDeal = dealsData?.find?.((item) => item.skuId === itemId);
  const dealPrice = skuDeal?.skuDealsInfo?.discountedPrice
    ? Number(skuDeal.skuDealsInfo.discountedPrice)
    : null;
  const hasActiveDeal =
    isDeal &&
    isDealsEnabled &&
    skuDeal?.skuDealsInfo?.dealAvailable &&
    dealPrice &&
    dealPrice < (totalPrice ?? 0);

  const submitToCart = () => {
    router.push(routePaths.cart);
  };

  const { addToCartCall, addingToCartStatus, cartSuccess } = useAddToCartWithMiniCart();
  const [addToCartStatus, setAddToCartStatus] = useState<string>('idle');
  const cartSuccessData = cartSuccess;

  useEffect(() => {
    setAddToCartStatus(addingToCartStatus);
  }, [addingToCartStatus]);

  const scrollIntoViewOptions: ScrollIntoViewOptions = {
    block: 'nearest',
    inline: 'nearest',
    behavior: 'auto',
  };
  const { data: storeDetailsData } = useStoreDetailsData();

  const addCertonaItemObject: AddCertonaItemObjectType = {
    productImage: properties?.imageUrl ?? '',
    cartSuccessData: cartSuccessData,
    productDetails: {
      productName: {
        productBrand: addToCartData?.brandName ?? '',
        productPart: addToCartData?.productPartNumber ?? '',
        quickNote: addToCartData?.productRepositoryId ?? '',
      },
      skuId: addToCartData?.skuId,
      partType: addToCartData?.originalPartTypeId?.toString(),
      productId: addToCartData?.productId,
    },
    submitToCart,
    productTitle: label ?? '',
    pageName: 'pdp',
  };

  const afterAddTocartSuccess = async () => {
    await fetchPaymentOptions({});
    setShowLoadingIndicators(false);
    setAddedToCart(true);
    const addToCartAnalyticsData = {
      price: totalPrice?.toFixed(2),
      partGroupId: addToCartData?.partGroupId,
      partTerminologyId: addToCartData?.partTerminologyId,
      ...addToCartData,
    };
    if (addToCartAnalyticsandSuccessMessage) {
      addToCartAnalyticsandSuccessMessage(addToCartAnalyticsData);
    }
  };
  const addToCartMutation = useAddToCart();

  const newAfterAddTocartSuccess = async () => {
    setShowLoadingIndicators(false);
    setAddedToCart(true);
    await fetchPaymentOptions({});
    const addToCartAnalyticsData = {
      price: totalPrice?.toFixed(2),
      ...addToCartData,
    };
    if (addToCartAnalyticsandSuccessMessage) {
      addToCartAnalyticsandSuccessMessage(addToCartAnalyticsData);
    }
    if (setAddedCertonaItemObject) {
      setAddedCertonaItemObject(addCertonaItemObject);
    }
  };

  useEffect(() => {
    if (onkeydown && focusedElement === dynamicRef) {
      focusedElement.scrollIntoView(scrollIntoViewOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedElement]);

  const handleClick = () => {
    actionHandler(label, itemId);
    replaceHistoryStateOptionsOnClick(
      historyStateOptionAttributes.latestSelectedPageYOffset,
      root.pageYOffset,
    );
  };

  const handleNavigateToPDP = (event: React.MouseEvent) => {
    event.preventDefault();
    handleClick();
    void router.push(properties.seoUrl);
  };
  const handleAddtoCartCtaClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    if (addedToCart) {
      return;
    }

    if (addToCartData?.productId && addToCartData?.productId !== 'undefined' && !addedToCart) {
      if (showAddToCartCTA && !isCart && !isVehicleDetails) {
        return handleAddtoCartNewCtaClick();
      }
      setShowLoadingIndicators(true);

      if (!addToCartMutation.isPending) {
        const addToCartRequest = {
          skuId: String(addToCartData?.skuId),
          productId: addToCartData?.partGroupId ?? addToCartData?.productId,
          quantity: '1',
          fulfillmentTypeId: addToCartData?.fulFillmentId,
          vehicleId: addToCartData?.vehicleId,
          storeNumber: String(storeDetailsData?.storeNumber),
        };
        await addToCartMutation.mutateAsync(addToCartRequest);
        void afterAddTocartSuccess();
      }
    } else {
      submitToCart();
    }
  };

  const handleAddtoCartNewCtaClick = () => {
    setShowLoadingIndicators(true);
    if (addToCartStatus !== 'loading') {
      const { pageName = '', pageType = '' } = root.utag_data ?? {};

      const onSuccess = () => {
        void newAfterAddTocartSuccess();
      };

      const onSettled = () => {
        setShowLoadingIndicators(false);
      };

      const addToCartAnalyticsDataForCall = {
        price: totalPrice?.toFixed(2) ?? '',
        customLinkName: `${pageType}-Certona:Add_to_Cart` as CustomLinkName,
        pageName: pageName,
        pageType: pageType,
        productRecommendationTypeList: 'Certona',
        productFindingMethod: 'Product Recommendation',
        productRecommendationInteraction: 'productRecommendationInteraction',
        productRecommendationType:
          certonaScheme || getProductRecommendationType(root.utag_data?.pageType, recTabNumber),
        productBrand: addToCartData?.brandName ?? '',
        productStrikeThroughPricing: ['0'],
        corePrice: corePrice,
        fulfillmentChange: '',
        originalPartTypeId: addToCartData?.originalPartTypeId,
        productRepositoryId: addToCartData?.productRepositoryId,
        productPartNumber: addToCartData?.productPartNumber,
        productFulfillmentOptionSeenByCustomer: Number(addToCartData?.fulFillmentId),
        ...addToCartData,
      };

      const addToCartRequest: AddToCartRequestType = {
        sku_id: String(addToCartData?.skuId),
        product_id: addToCartData?.partGroupId ?? addToCartData?.productId ?? '',
        quantity: '1',
        category_id: '',
        order_type: addToCartData?.fulFillmentId ?? ('' as OrderType),
        catalog_vehicle_id: addToCartData?.vehicleId ?? '',
        store_number: String(storeDetailsData?.storeNumber),
        orderType: '',
        requestFromCertona: true,
      };
      addToCartCall(
        addToCartRequest,
        addToCartAnalyticsDataForCall,
        catalogVehicleId,
        onSuccess,
        onSettled,
      );
    }
  };

  useEffect(() => {
    const skuId = pricingInfo?.skuId || itemId;
    if (skusPresentInCart?.includes(skuId)) {
      setAddedToCart(true);
    }
  }, [skusPresentInCart, itemId, pricingInfo]);

  if (!showCard) {
    return null;
  }

  return (
    <Grid
      item
      alignContent="space-between"
      direction="column"
      className={cx(styles.newLargePartCard, {
        [styles.partCardCTAEnabled]: certonaAddToCartEnabled,
      })}
    >
      <div
        onClick={() => {
          linkRef.current?.click();
        }}
      >
        <Grid item className={styles.imageContainer} xs={12}>
          <Image
            src={properties.imageUrl}
            alt=""
            className={styles.certonaPartImage}
            data-testid="part-img"
            width={100}
            height={100}
            loader={pureLoader}
          />
        </Grid>

        {totalPrice === null ? (
          <Grid className={styles.priceUnavailable} container lg={12} xs={12} justify="center">
            <span className={styles.pricingInfo}>{priceUnavailable}</span>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <View direction="column">
            {totalPrice ? (
              <View.Item>
                <View direction="row" data-testid="total-price" className={styles.pricingContainer}>
                  {typeof totalPrice === 'number' ? (
                    <View>
                      {isDeal && isDealsLoading && visibleDealSkuIds?.includes(itemId) ? (
                        <Skeleton width="80px" height="25px" borderRadius="small" />
                      ) : hasActiveDeal ? (
                        <View direction="row" wrap={false}>
                          <View.Item className={cx(styles.price, azCommonStyles['az-caption'])}>
                            <span className={styles.dealPrice}>${formatPrice(totalPrice)}</span>
                          </View.Item>
                          <View.Item className={styles.price}>${formatPrice(dealPrice)}</View.Item>
                        </View>
                      ) : (
                        <>
                          <View.Item className={styles.price}>
                            $
                            {corePrice
                              ? (formatPrice(totalPrice) ?? null)
                              : retailPrice
                                ? formatPrice(retailPrice)
                                : null}
                          </View.Item>
                          {totalPrice !== undefined && retailPrice !== totalPrice && !corePrice && (
                            <View.Item
                              className={cx(azCommonStyles['az-caption'], styles.totalPrice)}
                            >
                              {`$${formatPrice(totalPrice)}`}
                            </View.Item>
                          )}
                        </>
                      )}
                    </View>
                  ) : null}
                </View>
              </View.Item>
            ) : null}

            <View.Item gapBefore={1}>
              {averageRating && Number(averageRating) > 0 && totalReviews ? (
                <View direction="row" wrap={false} height="19px">
                  <View
                    direction="column"
                    justify="center"
                    height="100%"
                    className={styles.starRatingContainer}
                  >
                    <Rating
                      value={Number(averageRating)}
                      max={5}
                      showStarFractions
                      isCertonaPartCard={true}
                      ariaLabel={`${label} rated ${Number(averageRating).toFixed(
                        1,
                      )} out of 5 stars`}
                    />
                  </View>
                  <View.Item gapBefore={7 / 4} />
                  <View direction="column" justify="center" height="100%">
                    <Text
                      variant="subtitle-regular"
                      className={styles.starcSubtitleRegularOverride}
                    >
                      <span aria-hidden>{Number(averageRating).toFixed(1)}</span>
                      <span>{` (${totalReviews})`}</span>
                      <span className="sr-only"> {` ${lblTotalReviews}`}</span>
                    </Text>
                  </View>
                </View>
              ) : null}
            </View.Item>
            <View.Item gapBefore={1}>
              <Link
                data-testid="part-link"
                title={label}
                to={properties.seoUrl}
                aria-label={[
                  totalPrice === null
                    ? priceUnavailable
                    : typeof totalPrice === 'number'
                      ? hasActiveDeal
                        ? `$${dealPrice ? formatPrice(dealPrice) : '0'}, was $${formatPrice(totalPrice)}`
                        : `$${corePrice ? formatPrice(totalPrice) : formatPrice(retailPrice)}`
                      : null,

                  averageRating && Number(averageRating) > 0 && totalReviews
                    ? `${Number(averageRating).toFixed(
                        1,
                      )} out of 5 stars, ${totalReviews} ${lblTotalReviews}`
                    : null,

                  label,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                  setPageSpecificData({
                    productFindingMethod: 'Product Recommendation',
                    productRecommendationInteraction: 'productRecommendationInteraction',
                    pfmLocation: root.utag_data?.pageType,
                    productRecommendationType:
                      certonaScheme ||
                      getProductRecommendationType(root.utag_data?.pageType, recTabNumber),
                  });
                }}
                className={cx(styles.partLabel, {
                  [styles.partLabelAddToCart]: certonaAddToCartEnabled,
                })}
                ref={linkRef}
              >
                <div data-testid="part-label">{label}</div>
              </Link>
            </View.Item>
          </View>
        </Grid>
      </div>
      {certonaAddToCartEnabled && !isLoadingAddToCart && (
        <Grid
          container
          justify="center"
          alignContent="flex-end"
          xs={10}
          lg={14}
          className={styles.addtoCartContainer}
          onClick={shouldShowAddToCart ? handleAddtoCartCtaClick : handleNavigateToPDP}
        >
          <Button
            ariaLabel={
              !shouldShowAddToCart && !showLoadingIndicators
                ? seeOptionsForProduct
                : addedToCart
                  ? addedProductToCart
                  : addProductToCart
            }
            variant="outlined"
            shape="pill"
            size="small"
            customClass={cx(styles.addtoCartButton, {
              [styles.addedTocartButtonstyles]: addedToCart,
            })}
          >
            {!shouldShowAddToCart && !showLoadingIndicators && (
              <span>{lblSeeOptions.toUpperCase()}</span>
            )}
            {shouldShowAddToCart && !addedToCart && !showLoadingIndicators && (
              <>
                <Image
                  src={'/images/addcart.svg'}
                  alt=""
                  width={14}
                  height={14}
                  loader={pureLoader}
                />
                <span className={styles.addtoCartText}> {lblAdd}</span>
              </>
            )}

            {shouldShowAddToCart && addedToCart && !showLoadingIndicators && (
              <>
                <Image
                  src={'/images/green-tick-rounded.svg'}
                  alt=""
                  width={14}
                  height={14}
                  loader={pureLoader}
                />
                <span className={styles.addtoCartText}> {lblAdded}</span>
              </>
            )}

            {showLoadingIndicators && <LoadingIndicatorDots size={12} color={'#000'} />}
          </Button>
        </Grid>
      )}
    </Grid>
  );
}
